'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Upload, ImageIcon, X, Download } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import { createClient } from '@/lib/supabase/client';

interface GuestPhoto {
  id: string;
  public_url: string;
  uploader_name: string;
  caption?: string;
}

interface SelectedPhoto {
  file: File;
  preview: string;
  caption: string;
}

const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL!;
const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!;
const BUCKET = 'guest-photos';

async function uploadFileDirect(file: File, fileName: string): Promise<{ error: string | null }> {
  return new Promise((resolve) => {
    const url = `${SUPABASE_URL}/storage/v1/object/${BUCKET}/${encodeURIComponent(fileName)}`;
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Authorization', `Bearer ${SUPABASE_ANON_KEY}`);
    xhr.setRequestHeader('apikey', SUPABASE_ANON_KEY);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.setRequestHeader('x-upsert', 'false');
    xhr.setRequestHeader('Cache-Control', '3600');

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ error: null });
      } else {
        let msg = `HTTP ${xhr.status}`;
        try {
          const body = JSON.parse(xhr.responseText);
          msg = body?.message || body?.error || JSON.stringify(body) || msg;
        } catch {}
        resolve({ error: msg });
      }
    };

    xhr.onerror = () => resolve({ error: 'Network error during upload' });
    xhr.ontimeout = () => resolve({ error: 'Upload timed out' });
    xhr.timeout = 60000; // 60 second timeout

    xhr.send(file);
  });
}

export default function GuestGallery() {
  const supabase = createClient();
  const [photos, setPhotos] = useState<GuestPhoto[]>([]);
  const [uploaderName, setUploaderName] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState<SelectedPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [galleryLightboxIndex, setGalleryLightboxIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchPhotos();
  }, []);

  useEffect(() => {
    // Poll every 15 seconds to catch any changes (inserts or deletes)
    const interval = setInterval(() => {
      fetchPhotos();
    }, 15000);

    // Also refresh when the user returns to this tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchPhotos();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchPhotos = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('guest_photos')
        .select('id, public_url, uploader_name, caption')
        .order('uploaded_at', { ascending: false });

      if (error) {
        console.error('Error fetching guest photos:', error.message);
      } else {
        setPhotos(data || []);
      }
    } catch (err) {
      console.error('Unexpected error fetching photos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFiles = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const validFiles: SelectedPhoto[] = [];
    const invalid: string[] = [];

    fileArray.forEach((file) => {
      if (!file.type.startsWith('image/')) {
        invalid.push(file.name);
        return;
      }
      const preview = URL.createObjectURL(file);
      validFiles.push({ file, preview, caption: '' });
    });

    if (invalid.length > 0) {
      setError(`Skipped non-image files: ${invalid.join(', ')}`);
    } else {
      setError(null);
    }

    setSelectedPhotos((prev) => [...prev, ...validFiles]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files);
  };

  const removePhoto = (index: number) => {
    setSelectedPhotos((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const updateCaption = (index: number, caption: string) => {
    setSelectedPhotos((prev) =>
      prev.map((p, i) => (i === index ? { ...p, caption } : p))
    );
  };

  const handleUpload = async () => {
    if (selectedPhotos.length === 0) { setError('Please select at least one photo to upload.'); return; }
    if (!uploaderName.trim()) { setError('Please enter your name.'); return; }

    setUploading(true);
    setError(null);
    setUploadProgress({ current: 0, total: selectedPhotos.length });

    const newlyUploaded: GuestPhoto[] = [];

    for (let i = 0; i < selectedPhotos.length; i++) {
      const { file, caption } = selectedPhotos[i];
      setUploadProgress({ current: i + 1, total: selectedPhotos.length });

      const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;

      // Use direct REST API upload — bypasses SDK cookie/auth issues
      const { error: uploadError } = await uploadFileDirect(file, fileName);

      if (uploadError) {
        console.error('Storage upload error:', uploadError);
        setError(`Upload failed for photo ${i + 1}: ${uploadError}`);
        setUploading(false);
        setUploadProgress(null);
        return;
      }

      const publicUrl = `${SUPABASE_URL}/storage/v1/object/public/${BUCKET}/${fileName}`;

      const { data: inserted, error: dbError } = await supabase
        .from('guest_photos')
        .insert({
          storage_path: fileName,
          public_url: publicUrl,
          uploader_name: uploaderName.trim(),
          caption: caption.trim() || null,
        })
        .select('id, public_url, uploader_name, caption')
        .single();

      if (dbError) {
        console.error('DB insert error:', dbError);
        setError(`Could not save photo ${i + 1}: ${dbError.message}`);
        setUploading(false);
        setUploadProgress(null);
        return;
      }

      newlyUploaded.push(inserted);
    }

    // Revoke object URLs
    selectedPhotos.forEach((p) => URL.revokeObjectURL(p.preview));

    setPhotos((prev) => [...newlyUploaded.reverse(), ...prev]);
    setSelectedPhotos([]);
    setUploaderName('');
    setUploading(false);
    setUploadProgress(null);
    const count = newlyUploaded.length;
    setSuccessMsg(`${count} photo${count !== 1 ? 's' : ''} added to the gallery. Thank you!`);
    setTimeout(() => setSuccessMsg(null), 5000);
  };

  const handleDownload = async (url: string, uploaderName: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const ext = blob.type.split('/')[1] || 'jpg';
      const fileName = `photo-by-${uploaderName.replace(/\s+/g, '-').toLowerCase()}.${ext}`;
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch {
      // fallback: open in new tab
      window.open(url, '_blank');
    }
  };

  return (
    <section className="bg-off-white py-24 px-8 md:px-16 xl:px-24 border-t border-border">
      <div className="max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
          <div>
            <p className="section-label mb-4">Your Photographs</p>
            <h2 className="font-serif text-display-md text-faded-navy">
              Share Your Memories
            </h2>
            <p className="font-sans text-sm text-stone mt-4 max-w-lg leading-relaxed">
              We would love to see the day through your eyes. Upload your favourite photographs from the weekend and they will appear here for everyone to enjoy.
            </p>
          </div>
          <p className="font-sans text-xs text-stone tracking-[0.1em] shrink-0">
            {photos.length} photograph{photos.length !== 1 ? 's' : ''} shared
          </p>
        </div>

        {/* Upload area */}
        <div className="mb-16 border border-border bg-buttermilk p-8 md:p-10">
          <p className="text-xs tracking-[0.2em] uppercase font-sans text-stone mb-8">
            Upload Photos
          </p>

          {successMsg && (
            <div className="mb-6 px-5 py-4 bg-olive/10 border border-olive/30 text-olive font-sans text-sm">
              {successMsg}
            </div>
          )}

          {error && (
            <div className="mb-6 px-5 py-4 bg-red-50 border border-red-200 text-red-600 font-sans text-sm">
              {error}
            </div>
          )}

          {/* Drop zone */}
          <div
            className={`relative border-2 border-dashed transition-all duration-300 cursor-pointer flex flex-col items-center justify-center py-12 px-8 text-center mb-8 ${
              dragOver
                ? 'border-warm-sand bg-warm-sand/5' :'border-border hover:border-warm-sand/50'
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={28} strokeWidth={1} className="text-warm-sand mb-5" />
            <p className="font-sans text-sm text-stone mb-2">Drop photos here</p>
            <p className="font-sans text-xs text-stone/50">or tap to browse your camera roll</p>
            <p className="font-sans text-xs text-stone/40 mt-4">JPG, PNG, HEIC up to 20MB · Select multiple at once</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="sr-only"
            onChange={(e) => { if (e.target.files && e.target.files.length > 0) handleFiles(e.target.files); e.target.value = ''; }}
            aria-label="Upload photo files"
          />

          {/* Selected photos previews */}
          {selectedPhotos.length > 0 && (
            <div className="mb-8">
              <p className="text-xs tracking-[0.15em] uppercase font-sans text-stone mb-4">
                {selectedPhotos.length} photo{selectedPhotos.length !== 1 ? 's' : ''} selected
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3">
                {selectedPhotos.map((photo, index) => (
                  <div key={index} className="relative group">
                    <div
                      className="aspect-square overflow-hidden bg-border cursor-zoom-in"
                      onClick={() => setLightboxIndex(index)}
                    >
                      <img
                        src={photo.preview}
                        alt={`Selected photo ${index + 1}`}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute top-1 right-1 bg-deep-navy/70 text-white rounded-full p-1 hover:bg-deep-navy transition-colors duration-200"
                      aria-label={`Remove photo ${index + 1}`}
                    >
                      <X size={10} />
                    </button>
                    <input
                      type="text"
                      placeholder="Caption (optional)"
                      value={photo.caption}
                      onChange={(e) => updateCaption(index, e.target.value)}
                      className="mt-1 w-full text-xs font-sans px-2 py-1.5 border border-border bg-off-white text-stone placeholder-stone/40 focus:outline-none focus:border-warm-sand/60"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Name field + upload button */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
            <div>
              <label className="block text-xs tracking-[0.15em] uppercase font-sans text-stone mb-3">
                Your Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                placeholder="How you would like to be credited"
                value={uploaderName}
                onChange={(e) => setUploaderName(e.target.value)}
                className="input-field"
              />
            </div>
            <div>
              {uploadProgress && (
                <p className="font-sans text-xs text-stone mb-3 text-center md:text-left">
                  Uploading {uploadProgress.current} of {uploadProgress.total}…
                </p>
              )}
              <button
                type="button"
                onClick={handleUpload}
                disabled={uploading || selectedPhotos.length === 0}
                className="flex items-center justify-center gap-3 w-full py-4 text-xs tracking-[0.3em] uppercase font-sans bg-faded-navy text-buttermilk hover:bg-deep-navy transition-all duration-400 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ minHeight: '52px' }}
              >
                {uploading ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Uploading
                  </>
                ) : (
                  <>
                    <ImageIcon size={14} strokeWidth={1.5} />
                    {selectedPhotos.length > 1 ? `Share ${selectedPhotos.length} Photos` : 'Share Photo'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Lightbox for selected photo previews */}
        {lightboxIndex !== null && selectedPhotos[lightboxIndex] && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-deep-navy/90 p-4"
            onClick={() => setLightboxIndex(null)}
          >
            <button
              type="button"
              className="absolute top-4 right-4 text-off-white/80 hover:text-off-white bg-deep-navy/60 rounded-full p-2 transition-colors duration-200"
              onClick={() => setLightboxIndex(null)}
              aria-label="Close enlarged view"
            >
              <X size={22} />
            </button>
            {lightboxIndex > 0 && (
              <button
                type="button"
                className="absolute left-4 top-1/2 -translate-y-1/2 text-off-white/80 hover:text-off-white bg-deep-navy/60 rounded-full p-3 transition-colors duration-200"
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex - 1); }}
                aria-label="Previous photo"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
            )}
            {lightboxIndex < selectedPhotos.length - 1 && (
              <button
                type="button"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-off-white/80 hover:text-off-white bg-deep-navy/60 rounded-full p-3 transition-colors duration-200"
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex + 1); }}
                aria-label="Next photo"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            )}
            <img
              src={selectedPhotos[lightboxIndex].preview}
              alt={`Preview of selected photo ${lightboxIndex + 1}`}
              className="max-w-full max-h-[85vh] object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            {selectedPhotos[lightboxIndex].caption && (
              <p className="absolute bottom-6 left-1/2 -translate-x-1/2 font-sans text-sm text-off-white/80 bg-deep-navy/60 px-4 py-2 rounded">
                {selectedPhotos[lightboxIndex].caption}
              </p>
            )}
          </div>
        )}

        {/* Guest photo grid */}
        {loading ? (
          <div className="text-center py-20">
            <svg className="animate-spin w-8 h-8 text-warm-sand mx-auto" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : photos.length > 0 ? (
          <div className="columns-2 md:columns-3 xl:columns-4 2xl:columns-4 gap-1 space-y-1">
            {photos.map((photo, idx) => (
              <div key={photo.id} className="break-inside-avoid relative group overflow-hidden cursor-zoom-in" onClick={() => setGalleryLightboxIndex(idx)}>
                <AppImage
                  src={photo.public_url}
                  alt={`Photo shared by ${photo.uploader_name}`}
                  width={600}
                  height={400}
                  className="w-full h-auto object-cover transition-transform duration-600 group-hover:scale-105"
                  sizes="(max-width: 768px) 50vw, 25vw"
                />
                <div className="absolute inset-0 bg-deep-navy/50 opacity-0 group-hover:opacity-100 transition-opacity duration-400 flex flex-col justify-end p-4">
                  {photo.caption && (
                    <p className="font-sans text-xs text-off-white mb-1">{photo.caption}</p>
                  )}
                  <p className="font-sans text-xs text-off-white/60">{photo.uploader_name}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 border border-dashed border-border">
            <ImageIcon size={32} strokeWidth={1} className="text-warm-sand mx-auto mb-6" />
            <p className="font-serif text-xl text-faded-navy mb-3">No photos yet</p>
            <p className="font-sans text-sm text-stone">
              Be the first to share a memory from the weekend.
            </p>
          </div>
        )}

        {/* Gallery lightbox */}
        {galleryLightboxIndex !== null && photos[galleryLightboxIndex] && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-deep-navy/92 p-4"
            onClick={() => setGalleryLightboxIndex(null)}
          >
            {/* Close */}
            <button
              type="button"
              className="absolute top-4 right-4 text-off-white/80 hover:text-off-white bg-deep-navy/60 rounded-full p-2 transition-colors duration-200"
              onClick={() => setGalleryLightboxIndex(null)}
              aria-label="Close"
            >
              <X size={22} />
            </button>

            {/* Download */}
            <button
              type="button"
              className="absolute top-4 right-16 text-off-white/80 hover:text-off-white bg-deep-navy/60 rounded-full p-2 transition-colors duration-200"
              onClick={(e) => {
                e.stopPropagation();
                handleDownload(photos[galleryLightboxIndex].public_url, photos[galleryLightboxIndex].uploader_name);
              }}
              aria-label="Download photo"
              title="Download full-size photo"
            >
              <Download size={22} />
            </button>

            {/* Prev */}
            {galleryLightboxIndex > 0 && (
              <button
                type="button"
                className="absolute left-4 top-1/2 -translate-y-1/2 text-off-white/80 hover:text-off-white bg-deep-navy/60 rounded-full p-3 transition-colors duration-200"
                onClick={(e) => { e.stopPropagation(); setGalleryLightboxIndex(galleryLightboxIndex - 1); }}
                aria-label="Previous photo"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
            )}

            {/* Next */}
            {galleryLightboxIndex < photos.length - 1 && (
              <button
                type="button"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-off-white/80 hover:text-off-white bg-deep-navy/60 rounded-full p-3 transition-colors duration-200"
                onClick={(e) => { e.stopPropagation(); setGalleryLightboxIndex(galleryLightboxIndex + 1); }}
                aria-label="Next photo"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            )}

            {/* Image */}
            <img
              src={photos[galleryLightboxIndex].public_url}
              alt={`Photo by ${photos[galleryLightboxIndex].uploader_name}`}
              className="max-w-full max-h-[85vh] object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Caption / credit */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center">
              {photos[galleryLightboxIndex].caption && (
                <p className="font-sans text-sm text-off-white/90 bg-deep-navy/60 px-4 py-2 rounded mb-1">
                  {photos[galleryLightboxIndex].caption}
                </p>
              )}
              <p className="font-sans text-xs text-off-white/50">
                by {photos[galleryLightboxIndex].uploader_name}
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
